<template>
  <div class="component-wrapper-full">
    <div :class="`component-wrapper ${size}`">
      <span class="delete-button" :class="[!isUploaded && 'hide-delete-button']" @click="handleDeleteButton">
        <FIcon :name="'close'" size="0.8rem" color="#ffffff" />
      </span>

      <div class="app-upload-image">
        <v-card
          :outlined="true"
          :ripple="false"
          :class="[isUploaded && 'active-image']"
          rounded="0"
          class="image-upload-card"
        >
          <component
            :is="currentComponent"
            :imageSrc="imageSrc"
            :onImageSelected="handleOnImageUploaded"
            :multiple="multiple"
            :id="id"
            :accept="accept"
            :tabindex="tabindex"
          />
        </v-card>
      </div>
    </div>
    <AppDialog :is-visible="!!isDialogVisible" attach="#upload_image_attach" hide-overlay>
      <CropperComponent
        :imageSrc="imageSrc"
        v-if="!!imageSrc"
        class="cropper-component"
        ref="imageEditor"
        :handleCancelEditing="handleCancelEditing"
        :handleSaveEdit="handleSaveEdit"
      />
    </AppDialog>
    <v-overlay v-if="!!isDialogVisible" opacity="0.5" absolute light />
    <div id="upload_image_attach" />
  </div>
</template>

<script>
import UploadInitial from "./initial/index.vue";
import UploadedImage from "./uploaded/index.vue";
import CropperComponent from "./edit/index.vue";
import { $eventHub } from "@/components";
import { EventConstraints } from "@/enums/events.enum";

export default {
  name: "ImageUploader",

  components: {
    UploadInitial,
    UploadedImage,
    CropperComponent,
  },

  data() {
    return {
      currentComponent: UploadInitial,
      isUploaded: false,
      isDialogVisible: false,
      originalImage: null,
      imageSrc: null,
    };
  },

  mounted() {
    //Listen to upload and edit image event and trigger the corresponding functions once they emmited
    $eventHub.$on(EventConstraints.UPLOAD_IMAGE_EVENT, this.handleOnImageCreated);
    $eventHub.$on(EventConstraints.EDIT_IMAGE_EVENT, this.handleOnImageEdited);

    if (!this.editMode && !!this.src) {
      this.currentComponent = UploadedImage;
      this.isUploaded = true;
      this.imageSrc = this.src;
    }
  },

  destroyed() {
    $eventHub.$off(EventConstraints.UPLOAD_IMAGE_EVENT);
    $eventHub.$off(EventConstraints.EDIT_IMAGE_EVENT);
  },

  model: {
    prop: "imageSrc",
    event: "update",
  },

  props: {
    multiple: {
      type: Boolean,
      default: false,
    },

    accept: {
      type: String,
      default: "image/*",
    },

    tabindex: {
      type: [String, Number],
      default: 1,
    },

    id: {
      type: String,
      default: () => `app_input_upload_image`,
    },

    src: {
      type: String,
      default: null,
    },

    editMode: {
      type: Boolean,
      default: false,
    },

    size: {
      type: String,
      default: "small" || "x-small" || "large" || "x-large",
    },
  },

  methods: {
    /***
     * Save image edits and call cropImage function
     * @returns {void}
     */
    handleSaveEdit() {
      this.$refs.imageEditor.cropImage();
      this.handleToggleDialog();
    },

    /***
     * Change the current component and clone the current image source
     * @param  {event}
     * @returns {void}
     */
    handleOnImageUploaded({ target }) {
      const selectedImage = target.files[0];
      this.createBase64Image(selectedImage);
      this.originalImage = this.imageSrc;
      this.isUploaded = true;
      this.$nextTick(() => {
        this.currentComponent = UploadedImage;
      });
      target.value = "";
    },

    /***
     * Converts the image into base64 format and change the component
     * @param  {Blob} blob
     * @returns {void}
     */
    createBase64Image(blob) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageSrc = e.target.result;
        this.$emit("update", this.imageSrc);
      };
      reader.readAsDataURL(blob);
    },

    /***
     * update image source once finish edits
     * @returns {void}
     */
    handleOnImageCreated(image) {
      this.imageSrc = image;
      this.$emit("update", this.imageSrc);
    },

    /***
     * @returns {void}
     * Reset target and the current image and toggle the initial component
     */
    handleDeleteButton() {
      this.imageSrc = "";
      this.$emit("update", this.imageSrc);
      this.isUploaded = false;
      this.currentComponent = null;
      this.$nextTick(() => {
        this.currentComponent = UploadInitial;
        this.$forceUpdate();
      });
    },
    /***
     * Toggle dialog visibility
     * @returns {void}
     */
    handleToggleDialog() {
      this.isDialogVisible = false;
    },

    /***
     * Show edit image dialog
     * @returns {void}
     */
    handleOnImageEdited() {
      this.isDialogVisible = true;
    },

    /***
     * Reset image url and emmit reset event for cropper component and close the dialog
     * @returns {void}
     */
    handleCancelEditing() {
      this.$refs.imageEditor.clear();
      this.handleToggleDialog();
    },
  },
};
</script>

<style lang="scss" src="./styles.scss" scoped />
